// client/src/pages/admin/AdminCategoryList.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, List, ListItem, ListItemText, ListItemIcon, IconButton, Collapse, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Alert, CircularProgress, Stack, Tooltip, Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import EmptyState from '../../components/EmptyState';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import HideImageOutlinedIcon from '@mui/icons-material/HideImageOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useCategories } from '../../context/CategoryContext';
import { ICON_KEY_MAP, getCategoryIcon } from '../../constants/categoryIcons';
import { API_BASE_URL } from '../../utils/media';

const AdminCategoryList = () => {
  const { refreshCategories } = useCategories();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', iconKey: 'other', parentId: null });
  const [formError, setFormError] = useState('');

  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState('');
  const fileInputRef = useRef(null);

  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/categories`, { headers: authHeader() });
      if (!response.ok) throw new Error('Failed to load categories.');
      setCategories(await response.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const openForm = (parentId = null) => {
    setFormData({ name: '', iconKey: 'other', parentId });
    setFormError('');
    setFormOpen(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create category.');
      setFormOpen(false);
      fetchCategories();
    } catch (err) {
      setFormError(err.message);
    }
  };

  const handleToggleHidden = async (category) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/categories/${category.id}/visibility`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ hidden: !category.is_hidden }),
      });
      if (!response.ok) throw new Error('Failed to update category.');
      fetchCategories();
      refreshCategories();
    } catch (err) {
      alert(err.message);
    }
  };

  const savePhoto = async (category, file) => {
    try {
      const body = new FormData();
      if (file) body.append('image', file);
      const response = await fetch(`${API_BASE_URL}/api/admin/categories/${category.id}/image`, { method: 'PUT', headers: authHeader(), body });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Failed to save the photo.');
      fetchCategories();
      refreshCategories();
    } catch (err) {
      alert(err.message);
    }
  };

  const photoButton = (category) => (category.image_url ? (
    <Tooltip title="Remove homepage photo">
      <IconButton size="small" aria-label={`Remove photo from ${category.name}`} onClick={() => savePhoto(category, null)}>
        <HideImageOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  ) : (
    <Tooltip title="Add a homepage photo">
      <IconButton size="small" component="label" aria-label={`Add photo to ${category.name}`}>
        <AddPhotoAlternateOutlinedIcon fontSize="small" />
        <input hidden type="file" accept="image/*" onChange={(e) => { if (e.target.files[0]) savePhoto(category, e.target.files[0]); e.target.value = ''; }} />
      </IconButton>
    </Tooltip>
  ));

  const visibilityButton = (category) => (
    <Tooltip title={category.is_hidden ? 'Hidden from the shop. Click to show it.' : 'Visible in the shop. Click to hide it.'}>
      <IconButton size="small" onClick={() => handleToggleHidden(category)} aria-label={`${category.is_hidden ? 'Show' : 'Hide'} ${category.name}`}>
        {category.is_hidden ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category? Any subcategories under it will be deleted too.')) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/categories/${id}`, {
        method: 'DELETE',
        headers: authHeader(),
      });
      if (!response.ok) throw new Error('Failed to delete category.');
      fetchCategories();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/categories/template`, { headers: authHeader() });
      if (!response.ok) throw new Error('Failed to download template.');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'category-import-template.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setImportError(err.message);
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files[0];
    e.target.value = ''; // allow picking the same file again after a fix
    if (!file) return;
    setImportError('');
    setImportResult(null);
    setImporting(true);
    try {
      const body = new FormData();
      body.append('file', file);
      const response = await fetch(`${API_BASE_URL}/api/admin/categories/import`, {
        method: 'POST',
        headers: authHeader(),
        body,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to import categories.');
      setImportResult(data);
      fetchCategories();
    } catch (err) {
      setImportError(err.message);
    } finally {
      setImporting(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" fontWeight={700}>Categories</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button size="small" startIcon={<DownloadIcon />} onClick={handleDownloadTemplate}>
            Download Template
          </Button>
          <Button
            size="small" variant="outlined" startIcon={<UploadFileIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
          >
            {importing ? 'Importing…' : 'Import from Excel'}
          </Button>
          <input
            ref={fileInputRef} type="file" hidden
            accept=".xlsx,.xls,.csv"
            onChange={handleImportFile}
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openForm(null)}>New Category</Button>
        </Stack>
      </Box>

      {importError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setImportError('')}>{importError}</Alert>}
      {importResult && (
        <Alert
          severity={importResult.errors.length > 0 ? 'warning' : 'success'}
          sx={{ mb: 2 }}
          onClose={() => setImportResult(null)}
        >
          {importResult.message}
          {importResult.skipped > 0 && ` ${importResult.skipped} subcategor${importResult.skipped === 1 ? 'y' : 'ies'} already existed and ${importResult.skipped === 1 ? 'was' : 'were'} skipped.`}
          {importResult.errors.length > 0 && (
            <Box component="ul" sx={{ m: '4px 0 0', pl: 2 }}>
              {importResult.errors.map((e, i) => <li key={i}>{e}</li>)}
            </Box>
          )}
        </Alert>
      )}

      {categories.length === 0 ? (
        <EmptyState
          icon={<CategoryOutlinedIcon />}
          title="No categories yet"
          description="Add your first top-level category (e.g. RC Planes) to start organizing products for this store."
        />
      ) : (
        <List dense sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          {categories.map((cat) => {
            const Icon = getCategoryIcon(cat.icon_key);
            const isOpen = expanded[cat.id] ?? true;
            return (
              <React.Fragment key={cat.id}>
                <ListItem
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {photoButton(cat)}
                      {visibilityButton(cat)}
                      <IconButton size="small" onClick={() => openForm(cat.id)} aria-label="Add subcategory">
                        <AddIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(cat.id)} aria-label="Delete category">
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                      {cat.children.length > 0 && (
                        <IconButton
                          size="small"
                          onClick={() => setExpanded((e) => ({ ...e, [cat.id]: !isOpen }))}
                        >
                          <ExpandMoreIcon fontSize="small" sx={{ transform: isOpen ? 'rotate(180deg)' : 'none' }} />
                        </IconButton>
                      )}
                    </Box>
                  }
                >
                  <ListItemIcon sx={{ minWidth: 36 }}><Icon fontSize="small" color="primary" /></ListItemIcon>
                  <ListItemText
                    sx={{ opacity: cat.is_hidden ? 0.55 : 1 }}
                    primary={<>{cat.name}{Boolean(cat.is_hidden) && <Chip label="Hidden" size="small" sx={{ ml: 1 }} />}</>}
                    secondary={`${cat.children.length} subcategor${cat.children.length === 1 ? 'y' : 'ies'}`} />
                </ListItem>
                <Collapse in={isOpen}>
                  <List dense disablePadding>
                    {cat.children.map((sub) => (
                      <ListItem
                        key={sub.id}
                        sx={{ pl: 6 }}
                        secondaryAction={
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {visibilityButton(sub)}
                            <IconButton size="small" onClick={() => handleDelete(sub.id)} aria-label="Delete subcategory">
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        }
                      >
                        <ListItemText
                          sx={{ opacity: sub.is_hidden ? 0.55 : 1 }}
                          primary={<>{sub.name}{Boolean(sub.is_hidden) && <Chip label="Hidden" size="small" sx={{ ml: 1 }} />}</>}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          })}
        </List>
      )}

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} fullWidth maxWidth="xs">
        <Box component="form" onSubmit={handleCreate}>
          <DialogTitle>{formData.parentId ? 'New Subcategory' : 'New Category'}</DialogTitle>
          <DialogContent>
            {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
            <TextField
              label="Name" required fullWidth autoFocus sx={{ mt: 1, mb: 2 }}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
            {!formData.parentId && (
              <TextField
                select label="Icon" fullWidth
                value={formData.iconKey}
                onChange={(e) => setFormData({ ...formData, iconKey: e.target.value })}
              >
                {Object.keys(ICON_KEY_MAP).map((key) => (
                  <MenuItem key={key} value={key}>{key.replace('_', ' ')}</MenuItem>
                ))}
              </TextField>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained">Create</Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};

export default AdminCategoryList;
